import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
// Wire your existing API helpers here:
import { sendOtp, resendOtp, validateOtp, setLoginCookies, gatrack, getGA4CID, getQueryParamUtils } from "../../utils";

import "../../CssFiles/loginModal.css"


function OtpSingleInput({ value, onChange }) {
    const OTP_LEN = 4;
    const sanitizeOtp = (v) => (v || "").replace(/\D/g, "").slice(0, OTP_LEN);
    const validOtp = (v) => /^\d{4}$/.test(v);

    const handleChange = (e) => onChange(sanitizeOtp(e.target.value));
    const handlePaste = (e) => {
        const pasted = sanitizeOtp(e.clipboardData.getData("text"));
        if (pasted) {
            onChange(pasted);
            e.preventDefault();
        }
    };

    return (
        <input
            className="otp-single"
            type="tel"
            inputMode="numeric"
            pattern="\d*"
            autoComplete="one-time-code"
            name="one-time-code"
            maxLength={OTP_LEN}
            value={value}
            onChange={handleChange}
            onPaste={handlePaste}
            aria-label={`Enter ${OTP_LEN}-digit OTP`}
        />
    );
}



const LoginModalCtx = createContext(null);

export const useLoginModal = () => {
    const ctx = useContext(LoginModalCtx);
    if (!ctx) throw new Error("useLoginModal must be used within <LoginModalProvider>");
    return ctx;
};

/**
 * Provider renders a single modal portal and exposes openLoginModal(config).
 * Config: {
 *   phoneTitle, phoneSubtitle,
 *   otpTitle, otpSubtitle,
 *   otpLength = 6,
 *   allowClose = true,
 *   onSuccess?: (validateResult) => void
 * }
 */
export function LoginModalProvider({ children }) {
    const [isOpen, setIsOpen] = useState(false);
    const [step, setStep] = useState("phone");
    const [cfg, setCfg] = useState({
        phoneTitle: "Login to Continue",
        phoneSubtitle: "Quick verification",
        otpTitle: "Verify OTP",
        otpSubtitle: "Enter the code sent to your number",
        otpLength: 4,
        parent: "BL",
        allowClose: true,
        onSuccess: null,
    });

    const [expandedConsent, setExpandedConsent] = useState(false);
    const [phone, setPhone] = useState("");
    const [maskedPhone, setMaskedPhone] = useState("");
    const [loading, setLoading] = useState(false);
    const [otp, setOtp] = useState("");
    const [timer, setTimer] = useState(30);
    const [resendDisabled, setResendDisabled] = useState(true);
    const [formError, setFormError] = useState("");
    const [consentError, setConsentError] = useState("");
    const [consent, setConsent] = useState(true);
    const userGa4CID = getGA4CID();

    const otpRefs = useRef([]);

    const openLoginModal = useCallback((config = {}) => {
        setCfg(prev => ({ ...prev, ...config }));
        setPhone("");
        setMaskedPhone("");
        setOtp("");
        setTimer(30);
        setResendDisabled(true);
        setStep("phone");
        setFormError(""); // reset error
        setIsOpen(true);
    }, []);

    const closeLoginModal = useCallback(() => {
        if (!cfg.allowClose) return;
        setIsOpen(false);
    }, [cfg.allowClose]);

    useEffect(() => {
        if (!isOpen || step !== "otp") return;
        if (!resendDisabled) return;
        if (timer <= 0) {
            setResendDisabled(false);
            return;
        }
        const id = setTimeout(() => setTimer(t => t - 1), 1000);
        return () => clearTimeout(id);
    }, [isOpen, step, resendDisabled, timer]);

    
    const sanitizePhone = (v) => (v || "").trim().replace(/[^0-9]/g, "");
    const validPhone = (v) => /^[6-9]\d{9}$/.test(v);
    const maskPhone = (v) => (v ? v.replace(/^(\d{2})\d{6}(\d{2})$/, "+91 $1 **** **$2") : "");

    const sanitizeOtp = (v) => (v || "").trim().replace(/\s+/g, "");
    const validOtp = (v) => new RegExp(`^\\d{${cfg.otpLength}}$`).test(v);

    const onSendOtp = async (e) => {
        e?.preventDefault?.();
        const clean = sanitizePhone(phone);
        const full = window.location.pathname + window.location.search;
        if (!validPhone(clean)) {
            gatrack("Login Modal", "Invalid Phone Number", "Send OTP Attempt", "", "", full);
            return setFormError("Enter valid 10 digit number");
        }

        gatrack("Login Modal", "Valid Phone Number", "Send OTP Attempt", "", "", full);
        setLoading(true);
        setFormError("");

        gatrack("HT Login", "CLICK", "Send OTP", null, null, Object.fromEntries(getQueryParamUtils()))

        try {
            const resp = await sendOtp(clean);
            if (resp === "success") {
                setMaskedPhone(maskPhone(clean));
                setStep("otp");
                setTimer(30);
                setResendDisabled(true);
                setOtp("");
                setTimeout(() => otpRefs.current[0]?.focus(), 0);
                gatrack("Login Modal", "OTP Sent Successfully", "Send OTP Success", "", "", full);
            } else {
                gatrack("Login Modal", "OTP Send Failed", "Send OTP Failure", "", "", full);
                setFormError("Unable to send OTP. Please try again after some time.");
            }
        } finally {
            setLoading(false);
        }
    };

    const onVerifyOtp = async (e) => {
        e?.preventDefault?.();
        if(!consent){
            return setConsentError("Please check consent box to continue.");
        }
        setConsentError("");
        const cleanPhone = sanitizePhone(phone);
        const cleanOtp = sanitizeOtp(otp);
        const full = window.location.pathname + window.location.search;
        if (!validPhone(cleanPhone)) {
            gatrack("Login Modal", "Invalid Phone Number", "Veridy OTP", "", "", full);
            return setFormError("Enter valid 10 digit number")
        };
        if (!validOtp(cleanOtp)) {
            gatrack("Login Modal", "Invalid OTP Entered", "Veridy OTP", "", "", full);
            return setFormError(`Enter valid ${cfg.otpLength}-digit OTP`)
        };


        setLoading(true);
        setFormError("");
        gatrack("HT Login", "CLICK", "Verify OTP", null, null, Object.fromEntries(getQueryParamUtils()))
        try {
            const result = await validateOtp(cleanPhone, cleanOtp);
            if (result) {

                const glid = result?.resp1?.RESPONSE?.user_detail?.glusr_usr_id || "";
                const ak = result?.resp2?.t || "";
                const encNum = result?.encnum || "";

                if (glid && ak && cleanPhone && encNum) {
                    gatrack("Login Modal", "OTP Verified Successfully", "Verify OTP Success", glid, "", full);
                    setLoginCookies(glid, ak, cleanPhone, encNum);
                    window.dispatchEvent(new CustomEvent('ht:login', { detail: { glid, ak } }));
                } else {
                    console.log("Not setting cookies as values are incomplete ", glid, ak, cleanPhone, encNum);
                }

                cfg.onSuccess?.(glid, ak, cleanPhone);
                setIsOpen(false);
            } else {
                gatrack("Login Modal", "OTP Verification Failed", "Verify OTP Failure", "", "", full);
                setFormError("Invalid OTP. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    const onResendOtp = async () => {
        if (resendDisabled) return;
        const clean = sanitizePhone(phone);
        const full = window.location.pathname + window.location.search;
        if (!validPhone(clean)) {
            gatrack("Login Modal", "Invalid Phone Number", "Resend OTP Attempt", "", "", full);
            return setFormError("Enter valid 10 digit number");
        }

        gatrack("Login Modal", "Valid Phone Number", "Resend OTP Attempt", "", "", full);

        setResendDisabled(true);
        setTimer(30);
        gatrack("HT Login", "CLICK", "Resend OTP", null, null, Object.fromEntries(getQueryParamUtils()))
        try {
            const resp = await resendOtp(clean);
            if (resp !== "success") {
                gatrack("Login Modal", "OTP Resend Failed", "Resend OTP Failure", "", "", full);
                setFormError("Unable to resend OTP. Try again.");
            } 
            gatrack("Login Modal", "OTP Resent Successfully", "Resend OTP Success", "", "", full);
        } catch {
            setFormError("Unable to resend OTP. Try again.");
        }
    };

    const handleOtpBoxChange = (idx, val) => {
        setFormError(""); // clear error
        const digit = val.replace(/\D/g, "");
        const chars = otp.split("");
        chars[idx] = digit;
        const next = chars.join("").slice(0, cfg.otpLength);
        setOtp(next);
        if (digit && idx < cfg.otpLength - 1) otpRefs.current[idx + 1]?.focus();
    };

    const handleOtpKeyDown = (idx, e) => {
        if (e.key === "Backspace" && !otp[idx] && idx > 0) {
            otpRefs.current[idx - 1]?.focus();
        }
    };

    const handleOtpPaste = (e) => {
        const pasted = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, cfg.otpLength);
        if (!pasted) return;
        setOtp(pasted);
        setTimeout(() => otpRefs.current[Math.min(pasted.length, cfg.otpLength) - 1]?.focus(), 0);
        e.preventDefault();
    };

    const handlePhoneBlur = () => {
        const clean = sanitizePhone(phone);
        if(clean.length !== 10) {
            gatrack("Login Modal", "Invalid Phone Number on Blur", "Phone Input Blur", "", "", window.location.pathname + window.location.search);
        }
    }

    return (
        <LoginModalCtx.Provider value={{ openLoginModal, closeLoginModal }}>
            {children}
            {isOpen && (
                <div className="login-modal-overlay" onClick={closeLoginModal}>
                    <div className="login-modal" onClick={(e) => e.stopPropagation()}>
                        {cfg.allowClose && (
                            <button className="login-close" onClick={closeLoginModal} aria-label="Close">×</button>
                        )}
                        <div className="login-icon-circle" />
                        <h3 className="login-title">{step === "phone" ? cfg.phoneTitle : cfg.otpTitle}</h3>
                        <p className="login-subtitle">
                            {step === "phone" ? cfg.phoneSubtitle : (cfg.otpSubtitle || `We've sent a ${cfg.otpLength}-digit OTP to ${maskedPhone}`)}
                        </p>

                        {/* === PHONE STEP === */}
                        {step === "phone" ? (
                            <form onSubmit={onSendOtp} className="login-form">
                                <label className="login-label">Mobile Number</label>
                                <div className="login-input-wrap">
                                    <span className="login-input-prefix">+91</span>
                                    <input
                                        type="tel"
                                        inputMode="numeric"
                                        maxLength={10}
                                        className="login-input"
                                        placeholder="Enter your mobile number"
                                        value={phone}
                                        onChange={(e) => {
                                            setPhone(sanitizePhone(e.target.value));
                                            setFormError("");
                                        }}
                                        onPaste={(e) => {
                                            const paste = sanitizePhone(e.clipboardData.getData("text"));
                                            setPhone(paste.slice(0, 10));
                                            setFormError("");
                                            e.preventDefault();
                                        }}
                                        onBlur={handlePhoneBlur}
                                    />
                                </div>

                                {/* 🔴 Error message */}
                                {formError && <div className="login-error">{formError}</div>}

                                <button className="login-btn" type="submit" disabled={loading || !validPhone(phone)}>
                                    {loading ? "Sending..." : "Send OTP"}
                                </button>

                                <div className="login-note">Your data is 100% safe & secure</div>
                            </form>
                        ) : (
                            <form onSubmit={onVerifyOtp} className="login-form">
                                <label className="login-label otp-label">Enter {cfg.otpLength}-digit OTP</label>
                                <div className="otp-row">
                                    <OtpSingleInput value={otp} onChange={setOtp} />
                                </div>

                                <div className="resend-line">
                                    <span className="clock">🕒</span>{" "}
                                    {resendDisabled ? (
                                        <>Resend OTP in {timer}s</>
                                    ) : (
                                        <button type="button" className="resend-btn" onClick={onResendOtp}>
                                            Resend OTP
                                        </button>
                                    )}
                                </div>


                                    <div style={{ display: 'flex' }}>
                                        {/* Checkbox */}
                                        <span>
                                            <input
                                                type="checkbox"
                                                checked={consent}
                                                id="otp-checkBox"
                                                onChange={(e) => setConsent(e.target.checked)}
                                            />
                                        </span>

                                        {/* CONSENT TEXT + READ MORE */}
                                        <span style={{ fontSize: '10px', marginLeft: '4px' }}>
                                            <div className={!expandedConsent ? "collapsed-text" : ""}>
                                                {cfg.parent === "CREDIT_CARDS" ? (
                                                    <>
                                                        By proceeding, I agree to provide personal details and authorize HelloTrade to
                                                        obtain my Credit Information from bureau partners (
                                                        <a
                                                            href="https://www.hellotrade.com/terms-and-conditions#experian"
                                                            target="_blank"
                                                            rel="noopener"
                                                            className="tw-underline tw-underline-offset-2"
                                                            style={{ color: "blue" }}
                                                        >
                                                            Experian T&C
                                                        </a>
                                                        ). I also agree to HelloTrade{" "}
                                                        <a
                                                            href="/privacy-policy"
                                                            target="_blank"
                                                            rel="noopener"
                                                            className="tw-underline tw-underline-offset-2"
                                                            style={{ color: "blue" }}
                                                        >
                                                            Privacy Policy
                                                        </a>{" "}
                                                        and{" "}
                                                        <a
                                                            href="/terms-and-conditions"
                                                            target="_blank"
                                                            rel="noopener"
                                                            className="tw-underline tw-underline-offset-2"
                                                            style={{ color: "blue" }}
                                                        >
                                                            T&Cs
                                                        </a>
                                                        .{" "}
                                                        I confirm that I’m above 18 years of age and consent to, and authorise
                                                        IIL Digital Private Limited (‘Hellotrade’) to share my personal information
                                                        with Credilio Financial Technologies Private Limited (Credit Card(s)
                                                        aggregator partner) for evaluation and provision of Credit Card(s) on my
                                                        behalf. Further, I understand and agree that Credilio shall share my personal
                                                        information with Credit Card(s) Issuers / Banks and financial institutions for
                                                        the purpose of issuing of Credit Card(s). I hereby consent and authorise
                                                        Hellotrade to share my profile details, including personal and sensitive
                                                        personal information with above stated entities for Credit Card(s) application
                                                        processing on my behalf. I have read and understood the terms and conditions, privacy policy along with Credilio Financial Technologies Private Limited’s{" "}
                                                        <a
                                                            href="https://customer.credilio.in/v2/terms/?fromV1=true"
                                                            target="_blank"
                                                            rel="noopener"
                                                            className="tw-underline tw-underline-offset-2"
                                                            style={{ color: "blue" }}
                                                        >
                                                            terms and conditions
                                                        </a>
                                                        ,{" "}
                                                        <a
                                                            href="https://customer.credilio.in/v2/privacy-policy/?fromV1=true"
                                                            target="_blank"
                                                            rel="noopener"
                                                            className="tw-underline tw-underline-offset-2"
                                                            style={{ color: "blue" }}
                                                        >
                                                            privacy policy
                                                        </a>{" "}
                                                        and consent to the transmission of my personal and sensitive information for
                                                        credit card processing purposes. I also voluntarily agree to allow
                                                        above-stated Credit Card(s) Issuers to check my credit information, validate
                                                        my PAN from NSDL & share my said data with third party vendors for
                                                        verification.
                                                    </>
                                                ) : (
                                                    <>
                                                        By proceeding, I agree to provide personal details and authorize{" "}
                                                        <span className="tw-font-medium tw-text-foreground">
                                                            HelloTrade
                                                        </span>{" "}
                                                        to obtain my Credit Information from bureau partners{" "}
                                                        <span className="tw-italic tw-underline tw-underline-offset-2">
                                                            (
                                                            <a
                                                                href="https://www.hellotrade.com/terms-and-conditions#experian"
                                                                target="_blank"
                                                                rel="noopener"
                                                            >
                                                                Experian T&C
                                                            </a>
                                                            )
                                                        </span>
                                                        . I also agree to HelloTrade{" "}
                                                        <a
                                                            href="/privacy-policy"
                                                            target="_blank"
                                                            rel="noopener"
                                                            className="tw-underline tw-underline-offset-2"
                                                        >
                                                            Privacy Policy
                                                        </a>{" "}
                                                        and{" "}
                                                        <a
                                                            href="/terms-and-conditions"
                                                            target="_blank"
                                                            rel="noopener"
                                                            className="tw-underline tw-underline-offset-2"
                                                        >
                                                            T&Cs
                                                        </a>
                                                        .
                                                    </>
                                                )}
                                            </div>

                                            {/* Read More Toggle */}
                                            <span
                                                onClick={() => setExpandedConsent(!expandedConsent)}
                                                style={{
                                                    color: '#0066cc',
                                                    textDecoration: 'underline',
                                                    cursor: 'pointer',
                                                    display: 'inline-block',
                                                    marginTop: '2px'
                                                }}
                                            >
                                                {expandedConsent ? "Read less" : "Read more"}
                                            </span>
                                        </span>
                                    </div>

                                {/* 🔴 Error message */}
                                {formError && <div className="login-error">{formError}</div>}
                                {consentError && <div className="login-error">{consentError}</div>}


                                <button className="login-btn" type="submit" disabled={loading || !validOtp(otp)}>
                                    {loading ? "Verifying..." : "Verify & Continue"}
                                </button>

                            </form>
                        )}
                        <div className="login-footer-brand">
                            Trusted by SMEs across India · Secure · Fast · An IndiaMART Company
                        </div>
                    </div>
                </div>
            )}
        </LoginModalCtx.Provider>
    );
}

